import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  DefaultMetric,
  MetricDataType,
  MetricSign,
  MetricType,
} from 'src/app/domain/recovery/component/recovery-metrics-panel/entity/DefaultMetric';
import { DefaultMetricGroup } from 'src/app/domain/recovery/component/recovery-metrics-panel/entity/DefaultMetricGroup';
import { ModalButtons, SpinnerProps } from 'src/app/domain/shared/component/modal-container/modal-container.component';
import {
  BaseMetricForm,
  BaseMetricGroupForm,
  MetricDataPropertyForm,
  MetricPropertyForm,
} from '../../dto/MetricGroupForm';
import { MetricGroup } from '../../dto/MetricGroup';
import { Subject } from 'rxjs';
import { Metric } from '../../dto/Metric';
import { MetricComponentStateService } from '../../service/internal/metric-component-state.service';
import { NOTIFICATIONS, NotificationsService } from '../../../../core/service/notifications.service';
import {
  RecoveryMetricsService,
} from '../../../recovery/component/recovery-metrics-panel/service/recovery-metrics.service';
import { takeUntil } from 'rxjs/operators';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { Organization } from '../../../organization/dto/Organization';
import { KpiBusinessService } from '../../service/backend/kpi-business.service';
import {
  metricDataTypesWithIcons,
  MetricGroupCrudDataTypeWithIcon,
  MetricGroupCrudSignWithIcon,
  MetricGroupCrudType, MetricGroupDataType,
  metricSignsWithIcons,
  ModalTypeAndGroupInfo,
} from "./metric-group-crud-helper.service";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import {
  KpiMetricGroupDataTypeSelectorComponent,
} from "./kpi-metric-group-data-type-selector/kpi-metric-group-data-type-selector.component";

@Component({
  selector: 'app-metric-group-crud-base-modal',
  templateUrl: './metric-group-crud-base-modal.component.html',
  standalone: true,
  styleUrls: ['./metric-group-crud-base-modal.component.scss'],
  imports: [
    SharedComponentsModule,
    KpiMetricGroupDataTypeSelectorComponent,
  ],
})
export class MetricGroupCrudBaseModalComponent implements OnChanges, OnDestroy, OnInit {
  protected _destroyStream: Subject<void> = new Subject<void>();
  readonly metricGroupTypes: MetricGroupDataType[] = ['Numbers with balances', 'Any metrics'];
  readonly errorsForControls = new Map<string, string>([
    ['metricNameRequired', 'Metric name is missing'],
    ['invalidName', 'Cannot use "Starting" or "Ending" names'],
    ['duplicateMetric', 'Same metric name is used'],
    ['notEnoughMetrics', 'Add at least one metric to continue.'],
    ['duplicateGroup', 'Same group name is used.'],
  ]);

  modalText = {
    deleteGroupDescription: ['Note that this metric group will be deleted alongside with the data connected to it.'],
    deleteMetricDescription: ['Note that this metric will be deleted alongside with the data connected to it.'],
  };

  @Input() allGroups: DefaultMetricGroup[] | MetricGroup[] = [];
  @Input() modalType: MetricGroupCrudType;
  @Input() group: DefaultMetricGroup | MetricGroup;

  @Output() closeEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() confirmEmitter = new EventEmitter<ModalTypeAndGroupInfo>();
  @Output() deleteMetricGroup = new EventEmitter<void>();
  type: 'defaultMetrics' | 'metrics';

  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Cancel',
    apply: 'Apply',
    lowerLeft: 'Delete',
    lowerLeftIcon: 'delete.svg',
    lowerLeftClass: 'light-red',
  };

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  selectedOrg: Organization;
  signs: MetricGroupCrudSignWithIcon[] = metricSignsWithIcons;
  dataTypes: MetricGroupCrudDataTypeWithIcon[] = metricDataTypesWithIcons;
  metricsGroup: BaseMetricGroupForm;
  starting: BaseMetricForm;
  ending: BaseMetricForm;
  enableForAllCompanies: boolean = true;
  showEnableForAllCompanies: boolean = false;
  showFirstExplanationModal = false; // this modal is shown only when group is being created.
  metricGroupType: MetricGroupDataType = 'Numbers with balances';

  /**
   * By creating a new metric, we want to update the name with a next incremented value:
   * 'Metric 1' -> 'Metric 2' -> ...
   */
  internalMetricCounter = 1;

  showThinkTwiceModalForGroup = false;
  metricIndexForDeleteThinkTwiceModal: number;

  protected fb: FormBuilder;

  constructor(fb: FormBuilder,
    protected recoveryMetricService: RecoveryMetricsService,
    protected metricsService: KpiBusinessService,
    protected orgService: OrganizationsService,
    protected notificationService: NotificationsService,
    private metricStateService: MetricComponentStateService) {
    this.fb = fb;
  }

  ngOnInit(): void {
    this.orgService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrg = org;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.group && this.group) {
      this.setButtons();
      this.setStartingAndEnding();
      this.setModalType();
      this.createForm();
      if (this.modalType === 'create') {
        this.showFirstExplanationModal = true;
      }
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  close(): void {
    this.closeEmitter.emit();
    this.metricStateService.finishCRUDMetricGroup(false);
  }

  confirm(): void {
    const groupToSave: ModalTypeAndGroupInfo = {
      group: this.type === 'metrics' ? this.prepareCustomGroup() : this.prepareDefaultGroup(),
      modalType: this.group.uuid ? 'update' : 'create',
      enableForAllCompanies: this.enableForAllCompanies,
    };
    this.confirmEmitter.emit(groupToSave);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async initDeleteMetricGroup(): Promise<void> {
    this.showThinkTwiceModalForGroup = true;
  }

  get groupErrorMessage(): string | null {
    const errors = this.metricsGroup.controls.name.errors;
    if (!errors) {
      return null;
    }

    // Return the first matching error message.
    for (const errorName of Object.keys(errors)) {
      if (this.errorsForControls.has(errorName)) {
        return this.errorsForControls.get(errorName);
      }
    }

    return null;
  }

  changeModalType(metricGroupType: MetricGroupDataType, isRemoveAllMetricsEnabled = false): void {
    this.metricGroupType = metricGroupType;
    switch (metricGroupType) {
      // starting and ending are allowed only for standard metrics.
      case "Numbers with balances":
        this.starting.enable();
        this.ending.enable();
        break;
      default:
        this.starting.disable();
        this.ending.disable();
        break;
    }
    if (isRemoveAllMetricsEnabled) {
      this.removeAllMetrics();
    }
  }

  metricErrorMessage(metric: BaseMetricForm): string | null {
    const errors = metric.controls.name.errors;
    if (!errors) {
      return null;
    }

    // Return the first matching error message.
    for (const errorName of Object.keys(errors)) {
      if (this.errorsForControls.has(errorName)) {
        return this.errorsForControls.get(errorName);
      }
    }

    return null;
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    const metrics = this.metricsGroup.controls.metrics;
    moveItemInArray(metrics.value, event.previousIndex, event.currentIndex);
    moveItemInArray(metrics.controls, event.previousIndex, event.currentIndex);
    this.updatePriorities();
  }

  deleteGroup(isConfirmed: boolean): void {
    if (isConfirmed) {
      this.deleteMetricGroup.emit();
    }
    this.showThinkTwiceModalForGroup = false;
  }

  /**
   * Handles the change event when the provided FormControl is selected or deselected.
   * Depending on the form control and isSelected status, corresponding tasks are executed.
   *
   * @param control form control that has been changed
   * @param otherControl the other form control that needs to be updated
   * @param isStarting indicator if the control is representing starting metric, to set selected for ending metric
   */
  handleChange(control: BaseMetricForm, otherControl: BaseMetricForm, isStarting: boolean): void {
    if (control.value.isSelected) {
      control.controls.name?.enable();
      if (isStarting) {
        otherControl.patchValue({ isSelected: true });
      }
    } else {
      control.controls.name?.disable();
      if (!isStarting) {
        otherControl.patchValue({ isSelected: false });
      }
    }
    this.metricsGroup.controls.name.updateValueAndValidity();
  }

  setSign(metric: FormGroup, sign: MetricGroupCrudSignWithIcon): void {
    const dataProps = metric.controls.dataProps as MetricDataPropertyForm;
    dataProps.controls.sign.setValue(sign.sign);
    dataProps.controls.iconNameAndStylingLeft.setValue(sign.iconNameAndStylingLeft);
  }

  setType(metric: FormGroup, dataType: MetricGroupCrudDataTypeWithIcon): void {
    const dataProps = metric.controls.dataProps as MetricDataPropertyForm;
    dataProps.controls.dataType.setValue(dataType.dataType);
    dataProps.controls.sign.setValue(dataType.sign);
    dataProps.controls.iconNameAndStylingLeft.setValue(dataType.iconNameAndStylingLeft);
  }

  addMetric(m: DefaultMetric | Metric): void {
    const metric = this.createBaseMetricForm(m, false);
    metric.controls.priority.setValue(m ? m.priority : this.metricsGroup.controls.metrics.value.length + 2);
    this.metricsGroup.controls.metrics.push(metric);
  }

  deleteMetricFromMetricGroupAndCloseModal(isSuccessful: boolean): void {
    if (isSuccessful) {
      this.deleteMetricFromMetricGroup(this.metricIndexForDeleteThinkTwiceModal);
    }
    this.metricIndexForDeleteThinkTwiceModal = undefined;
  }

  deleteMetricFromMetricGroup(index: number): void {
    this.metricsGroup.controls.metrics.removeAt(index);
    this.metricsGroup.controls.name.updateValueAndValidity();
  }

  copyMetricGroup(i: number): void {
    const metricToCopy = this.metricsGroup.value.metrics[i];
    const newMetric: BaseMetricForm = new FormGroup({
      uuid: new FormControl(metricToCopy.uuid),
      name: new FormControl(metricToCopy.name),
      priority: new FormControl(metricToCopy.priority),
      isSelected: new FormControl(metricToCopy.isSelected),
      props: new FormGroup({
        isHidden: new FormControl<boolean>(metricToCopy.props.isHidden),
        type: new FormControl<MetricType>(metricToCopy.props.type),
      }),
      dataProps: new FormGroup({
        sign: new FormControl<MetricSign>(metricToCopy.dataProps.sign),
        dataType: new FormControl<MetricDataType>(metricToCopy.dataProps.dataType),
        iconNameAndStylingLeft: new FormControl<string>(metricToCopy.dataProps.iconNameAndStylingLeft),
      }),
    });
    this.metricsGroup.controls.metrics.insert(i + 1, newMetric);
    this.updatePriorities();
    this.metricsGroup.controls.name.updateValueAndValidity();
  }


  toggleEnableForAllCompanies(value: boolean): void {
    this.enableForAllCompanies = value;
  }

  getTypeSelectedItem(metric: BaseMetricForm): MetricGroupCrudDataTypeWithIcon {
    return this.dataTypes.find((v) => v.dataType === metric.value.dataProps.dataType);
  }

  selectMetricGroupDataType(type: MetricGroupDataType): void {
    this.changeModalType(type, false);
    this.showFirstExplanationModal = false;
  }

  private updatePriorities(): void {
    this.metricsGroup.controls.metrics.controls.forEach((m, i) => {
      const priority = m.controls.priority;
      priority.setValue(i + 2);
      // +2 because this should have the biggest priority because it's ending
      // +1 for the starting, +1 for the ending => total +2
    });
  }

  private createForm(): void {
    const priority = this.group.uuid ? this.group.priority : this.allGroups.length + 1;
    this.metricsGroup = this.fb.group({
      uuid: new FormControl(this.group.uuid),
      priority: new FormControl(priority),
      name: new FormControl(this.group.name, { validators: [Validators.required, this.groupNameValidator()] }),
      metrics: this.setMetricsList(),
    });
    if (!this.group.uuid) {
      this.addMetric(null);
    }
  }

  private setModalType(): void {
    const isNonNumberMetricTypeProvided = this.group[this.type]
      .some((metric) => metric.dataProps.dataType !== MetricDataType.NUMBER);
    if (isNonNumberMetricTypeProvided) {
      this.changeModalType("Any metrics", false);
    }
  }

  private setMetricsList(): FormArray {
    return this.fb.array(
      this.group[this.type]
        .filter((metric) => metric.props.type !== MetricType.STARTING && metric.props.type !== MetricType.ENDING)
        .map((metric) => this.createBaseMetricForm(metric, false)),
    );
  }

  private createBaseMetricForm(metric: DefaultMetric | Metric, isDefaultMetric: boolean): BaseMetricForm {
    const isOtherTypeSelected = this.metricGroupType === 'Any metrics';
    const metricDataProps: MetricDataPropertyForm = this.fb.group({
      sign: new FormControl(metric ? metric.dataProps.sign : MetricSign.ANY),
      dataType: new FormControl(metric?.dataProps.dataType ?? (isOtherTypeSelected ? MetricDataType.STRING : MetricDataType.NUMBER)),
      iconNameAndStylingLeft: new FormControl(metric?.dataProps.iconNameAndStylingLeft ?? (isOtherTypeSelected ? 'abc.svg' : 'plus-minus.svg')),
    });
    const metricProperty: MetricPropertyForm = this.fb.group({
      isHidden: new FormControl(metric?.props.isHidden ?? (this.type !== 'metrics')),
      type: new FormControl(metric ? metric.props.type : MetricType.REGULAR),
    });
    return this.fb.group({
      uuid: new FormControl(metric ? metric.uuid : null),
      name: new FormControl(metric ? metric.name :
        ('Metric ' + this.internalMetricCounter++), { validators: [Validators.required, this.metricNameValidator(isDefaultMetric)] }),
      // because starting is 1
      priority: new FormControl(metric ? metric.priority : this.metricsGroup?.controls.metrics?.value.length + 2),
      props: metricProperty,
      dataProps: metricDataProps,
      isSelected: new FormControl(false),
    });
  }

  private prepareDefaultGroup(): DefaultMetricGroup {
    const groupToSave = new DefaultMetricGroup();
    groupToSave.uuid = this.metricsGroup.value.uuid;
    groupToSave.name = this.metricsGroup.value.name;
    groupToSave.priority = this.metricsGroup.value.priority;
    groupToSave.defaultMetrics = [];
    if (this.starting.enabled && this.starting.value.isSelected) {
      this.addStartingAndEndingForDefaultGroup(groupToSave, true);
    }
    (this.metricsGroup.controls.metrics.value as DefaultMetric[]).forEach((m) => groupToSave.defaultMetrics.push(m));
    if (this.ending.enabled && this.ending.value.isSelected) {
      this.addStartingAndEndingForDefaultGroup(groupToSave, false);
    }
    groupToSave.defaultMetrics.forEach((m) => m.props.isHidden = true);
    return groupToSave;
  }


  private addStartingAndEndingForDefaultGroup(groupToSave: DefaultMetricGroup, isStarting: boolean): void {
    if (isStarting) {
      this.starting.value.priority = 1;
      groupToSave.defaultMetrics.push(this.starting.value as DefaultMetric);
      return;
    }
    this.ending.value.priority = this.metricsGroup.controls.metrics.value.length + 2;
    groupToSave.defaultMetrics.push(this.ending.value as DefaultMetric);
  }

  private prepareCustomGroup(): MetricGroup {
    const groupToSave = new MetricGroup();
    groupToSave.uuid = this.metricsGroup.value.uuid;
    groupToSave.name = this.metricsGroup.value.name;
    groupToSave.priority = this.group.uuid ? this.group.priority : this.allGroups.length + 1;
    groupToSave.organizationUUID = this.selectedOrg.uuid;
    groupToSave.metrics = [];
    if (this.starting.enabled && this.starting.value.isSelected) {
      this.addStartingAndEndingForCustomGroup(groupToSave, true);
    }

    (this.metricsGroup.controls.metrics.value as Metric[]).forEach((metric) => {
      const metricToSave = new Metric();
      metricToSave.uuid = metric.uuid;
      metricToSave.name = metric.name;
      metricToSave.priority = metric.priority;
      metricToSave.props = metric.props;
      metricToSave.dataProps = metric.dataProps;
      groupToSave.metrics.push(metricToSave);
    });
    if (this.ending.enabled && this.ending.value.isSelected) {
      this.addStartingAndEndingForCustomGroup(groupToSave, false);
    }
    return groupToSave;
  }

  private addStartingAndEndingForCustomGroup(groupToSave: MetricGroup, isStarting: boolean): boolean {
    if (isStarting) {
      this.starting.value.priority = 1;
      const starting = this.starting.value as Metric;
      groupToSave.metrics.push(starting);
      return;
    }
    const ending = this.ending.value as Metric;
    ending.priority = this.metricsGroup.controls.metrics.value.length + 2;
    groupToSave.metrics.push(ending);
  }

  private setStartingAndEnding(): void {
    if (!this.group.uuid) {
      this.starting = this.setStartingOrEnding(null, true, MetricType.STARTING);
      this.ending = this.setStartingOrEnding(null, true, MetricType.ENDING);
      this.internalMetricCounter = 1; // Starting and ending did increment the value to 3. We need to reset to 1.
      return;
    }
    const starting = this.group[this.type].find((m) => m.props.type === MetricType.STARTING);
    const ending = this.group[this.type].find((m) => m.props.type === MetricType.ENDING);
    this.starting = starting ?
      this.setStartingOrEnding(starting, true, MetricType.STARTING) : this.setStartingOrEnding(null, false, MetricType.STARTING);
    this.ending = ending ?
      this.setStartingOrEnding(ending, true, MetricType.ENDING) : this.setStartingOrEnding(null, false, MetricType.ENDING);
  }

  private setStartingOrEnding(metric: Metric | DefaultMetric, isSelected: boolean, metricType: MetricType): BaseMetricForm {
    const metricForm = this.createBaseMetricForm(metric, true);
    const priority = metric?.priority ?? (metricType === MetricType.STARTING ? 1 : this.group[this.type].length + 2);
    metricForm.controls.isSelected.setValue(isSelected);
    metricForm.controls.name.setValue(metric ? metric.name : metricType);
    metricForm.controls.priority.setValue(priority);
    metricForm.controls.props.patchValue({
      type: metric ? metric.props.type : metricType,
      isHidden: metric?.props.isHidden ?? (this.type !== 'metrics'),
    });
    return metricForm;
  }

  private groupNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.metricsGroup) {
        return null;
      }
      const groupName = control.value?.toLowerCase().trim();

      if (!groupName || !this.allGroups.length) {
        return null;
      }

      const hasMetrics = this.metricsGroup.controls.metrics.value.length > 0;
      if (!hasMetrics) {
        return { notEnoughMetrics: true };
      }

      // if the name is not the initial name by update.
      if (groupName !== this.group?.name?.toLowerCase()) {
        const isDuplicate = this.allGroups.some((group) => group.name.toLowerCase() === groupName);
        return isDuplicate ? { duplicateGroup: true } : null;
      }
      return null;
    };
  }

  private metricNameValidator(isDefaultMetric: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.metricsGroup) {
        return null;
      }

      const name = control?.value?.toLowerCase().trim();

      if (!name) {
        return { metricNameRequired: true };
      }

      if (isDefaultMetric) {
        return null;
      }

      if (name === MetricType.STARTING.toLowerCase() || name === MetricType.ENDING.toLowerCase()) {
        return { invalidName: true };
      }

      const isDuplicate = this.metricsGroup.getRawValue().metrics
        .filter((m) => m.name.toLowerCase() === name)
        .length > 1;
      if (isDuplicate) {
        return { duplicateMetric: true };
      }

      this.metricsGroup.controls.name.updateValueAndValidity();
      return null;
    };
  }

  private setButtons(): void {
    this.modalButtons.lowerLeft = this.group.uuid ? 'Delete' : null;
  }

  private removeAllMetrics(): void {
    this.metricsGroup.controls.metrics.clear();
    this.internalMetricCounter = 1;
    this.addMetric(null);
    if (this.modalType === 'update') {
      this.notificationService.warning(NOTIFICATIONS.METRICS_RESET_WARNING);
    }
  }
}
